import React, { useState, useEffect } from "react";
import { signOut } from "firebase/auth";
import { getFirestore, doc, setDoc, collection, addDoc, getDocs, deleteDoc, onSnapshot } from "firebase/firestore";
import app, { vicePresidentAuth } from "../../firebase";
import Select from 'react-select';
import { useNavigate } from "react-router-dom";
import { createManagedAccount } from "../../services/studentAccountCreation";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";

const auth = vicePresidentAuth;
const db = getFirestore(app);

export default function VicePresidentDashboard() {
  // Page navigation state
  const navigate = useNavigate();

  // Auth state listener - maintain session and redirect if not authenticated
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate("/academics");
      }
    });
    return unsubscribe;
  }, [navigate]);

  // Toggle for manual school entry
  const [manualSchool, setManualSchool] = useState(false);
  // Schools state for dropdown
  const [schools, setSchools] = useState([]);

  // Fetch schools for dropdown
  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const schoolsSnap = await getDocs(collection(db, "schools"));
        setSchools(schoolsSnap.docs.map(d => d.data().name));
      } catch (err) {
        console.error("failed to fetch schools", err);
      }
    };
    fetchSchools();
  }, []);

  // Page navigation state
  const [page, setPage] = useState("addDean"); // "addDean", "addRoom", "viewSchedule"

  // Add Dean state
  const [deanEmail, setDeanEmail] = useState("");
  const [deanPassword, setDeanPassword] = useState("");
  const [deanFirstName, setDeanFirstName] = useState("");
  const [deanMiddleName, setDeanMiddleName] = useState("");
  const [deanLastName, setDeanLastName] = useState("");
  const [deanSchool, setDeanSchool] = useState("");
  const [deanMsg, setDeanMsg] = useState("");

  // Add Room state
  const [roomName, setRoomName] = useState("");
  const [roomType, setRoomType] = useState("");
  const [roomDepartment, setRoomDepartment] = useState([]);
  const [roomMsg, setRoomMsg] = useState("");
  const [departments, setDepartments] = useState([]);
  const [rooms, setRooms] = useState([]); // Rooms state
  const [roomFilterName, setRoomFilterName] = useState("");
  const [roomFilterType, setRoomFilterType] = useState("");
  const [roomFilterDept, setRoomFilterDept] = useState("");
  const [editRoomId, setEditRoomId] = useState(null);
  const [editRoomData, setEditRoomData] = useState({ roomName: "", roomType: "", departments: [] });
  const [roomToDelete, setRoomToDelete] = useState(null);
  const [roomDeleteError, setRoomDeleteError] = useState("");
  const [roomTablePage, setRoomTablePage] = useState(1);
  const roomsPerPage = 15;

  // Profile / header
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profilePicUrl = "https://ui-avatars.com/api/?name=Vice+President";
  const userDisplayName = "Vice President";

  // Fetch departments for dropdown
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const deptSnap = await getDocs(collection(db, "departments"));
        setDepartments(deptSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("failed to fetch departments", err);
      }
    };
    fetchDepartments();
  }, []);

  // Fetch rooms for display table - Real-time listener
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "rooms"), (roomsSnap) => {
      setRooms(roomsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => {
      console.error("failed to fetch rooms", err);
    });
    return unsubscribe;
  }, []);

  const roomTypeOptions = Array.from(new Set(rooms.map(room => room.roomType).filter(Boolean)));

  // Add Dean handler
  const handleAddDean = async (e) => {
    e.preventDefault();
    setDeanMsg("");
    try {
      await createManagedAccount({
        caller: auth.currentUser,
        email: deanEmail,
        displayName: `${deanFirstName} ${deanLastName}`.trim(),
        password: deanPassword,
        staffProfile: {
          firstName: deanFirstName,
          middleName: deanMiddleName,
          lastName: deanLastName,
          email: deanEmail,
          school: deanSchool,
          role: "Dean"
        }
      });
      await setDoc(doc(db, "schools", deanSchool), { name: deanSchool }, { merge: true });
      setDeanMsg("Dean account created successfully!");
      setDeanEmail(""); setDeanPassword(""); setDeanFirstName(""); setDeanMiddleName(""); setDeanLastName(""); setDeanSchool("");
      // refresh schools
      const schoolsSnap = await getDocs(collection(db, "schools"));
      setSchools(schoolsSnap.docs.map(d => d.data().name));
    } catch (err) {
      setDeanMsg(err.message || "Failed to create dean");
    }
  };

  // Add Room handler
  const handleAddRoom = async (e) => {
    e.preventDefault();
    setRoomMsg("");
    
    // Check if room name already exists
    if (rooms.some(room => room.roomName.toLowerCase() === roomName.toLowerCase())) {
      setRoomMsg("Error: Room with this name already exists!");
      return;
    }
    
    try {
      await addDoc(collection(db, "rooms"), {
        roomName,
        roomType,
        departments: roomDepartment
      });
      setRoomMsg("Room added successfully!");
      setRoomName(""); setRoomType(""); setRoomDepartment([]);
      const roomsSnap = await getDocs(collection(db, "rooms"));
      setRooms(roomsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      setRoomMsg(err.message || "Failed to add room");
    }
  };

  const handleDeleteRoom = async () => {
    if (!roomToDelete) return;
    try {
      await deleteDoc(doc(db, "rooms", roomToDelete.id));
      const roomsSnap = await getDocs(collection(db, "rooms"));
      setRooms(roomsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setRoomToDelete(null);
      setRoomDeleteError("");
    } catch (err) {
      setRoomDeleteError("Failed to delete room: " + (err.message || err));
    }
  };

  const handleEditRoom = (room) => {
    setEditRoomId(room.id);
    setEditRoomData({
      roomName: room.roomName || "",
      roomType: room.roomType || "",
      departments: Array.isArray(room.departments) ? room.departments : [room.department || ""]
    });
  };

  const handleSaveEditRoom = async () => {
    try {
      await setDoc(doc(db, "rooms", editRoomId), {
        roomName: editRoomData.roomName,
        roomType: editRoomData.roomType,
        departments: editRoomData.departments
      }, { merge: true });
      setEditRoomId(null);
      setEditRoomData({ roomName: "", roomType: "", departments: [] });
      const roomsSnap = await getDocs(collection(db, "rooms"));
      setRooms(roomsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      alert("Failed to save room: " + (err.message || err));
    }
  };

  const filteredRooms = rooms.filter(room =>
    (!roomFilterName || (room.roomName || "").toLowerCase().includes(roomFilterName.toLowerCase())) &&
    (!roomFilterType || (room.roomType || "").toLowerCase().includes(roomFilterType.toLowerCase())) &&
    (!roomFilterDept || (Array.isArray(room.departments) ? room.departments.join(", ") : room.department || "").toLowerCase().includes(roomFilterDept.toLowerCase()))
  );

  // Layout similar to StudentDashboard: sidebar, header, main content
  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5", display: "flex", flexDirection: "row" }}>
      {/* Sidebar */}
      <div style={{ width: 220, background: "#155c2b", color: "#fff", display: "flex", flexDirection: "column", alignItems: "stretch", padding: "32px 0", minHeight: "100vh", boxShadow: "2px 0 8px #eee" }}>
        <div style={{ fontWeight: 700, fontSize: 22, textAlign: "center", marginBottom: 40, letterSpacing: 2 }}>ENROLLEASE</div>
        
        <button
          onClick={() => setPage("addDean")}
          style={{
            background: page === "addDean" ? "#e67e22" : "transparent",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            padding: "14px 0",
            fontWeight: 600,
            fontSize: 17,
            cursor: "pointer",
            textAlign: "left",
            paddingLeft: 32,
            marginBottom: 8,
            marginRight: 12,
            transition: "background 0.2s, color 0.2s",
            boxShadow: page === "addDean" ? "0 2px 8px #e67e22aa" : "none"
          }}
          onMouseEnter={(e) => !page === "addDean" && (e.target.style.background = "rgba(230, 126, 34, 0.15)")}
          onMouseLeave={(e) => !page === "addDean" && (e.target.style.background = "transparent")}
        >
          Add Dean
        </button>

        <button
          onClick={() => setPage("addRoom")}
          style={{
            background: page === "addRoom" ? "#e67e22" : "transparent",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            padding: "14px 0",
            fontWeight: 600,
            fontSize: 17,
            cursor: "pointer",
            textAlign: "left",
            paddingLeft: 32,
            marginBottom: 8,
            marginRight: 12,
            transition: "background 0.2s, color 0.2s",
            boxShadow: page === "addRoom" ? "0 2px 8px #e67e22aa" : "none"
          }}
          onMouseEnter={(e) => !page === "addRoom" && (e.target.style.background = "rgba(230, 126, 34, 0.15)")}
          onMouseLeave={(e) => !page === "addRoom" && (e.target.style.background = "transparent")}
        >
          Add Room
        </button>

        <button
          onClick={() => setPage("viewSchedule")}
          style={{
            background: page === "viewSchedule" ? "#e67e22" : "transparent",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            padding: "14px 0",
            fontWeight: 600,
            fontSize: 17,
            cursor: "pointer",
            textAlign: "left",
            paddingLeft: 32,
            marginBottom: 8,
            marginRight: 12,
            transition: "background 0.2s, color 0.2s",
            boxShadow: page === "viewSchedule" ? "0 2px 8px #e67e22aa" : "none"
          }}
          onMouseEnter={(e) => !page === "viewSchedule" && (e.target.style.background = "rgba(230, 126, 34, 0.15)")}
          onMouseLeave={(e) => !page === "viewSchedule" && (e.target.style.background = "transparent")}
        >
          View Schedule
        </button>

        <div style={{ flex: 1 }} />
      </div>

      {/* Main */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        {/* Header */}
        <div style={{ height: 80, background: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", padding: "0 40px", position: "relative" }}>
          <h1 style={{ margin: "0", fontSize: 24, color: "#155c2b", fontWeight: 600 }}>Vice President Dashboard</h1>
          
          <div style={{ display: "flex", alignItems: "center", position: "relative", gap: 16 }}>
            <span style={{ fontWeight: 600, fontSize: 18, color: "#155c2b" }}>{userDisplayName}</span>
            <button
              onClick={() => setProfileMenuOpen(!profileMenuOpen)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                display: "flex",
                alignItems: "center",
              }}
            >
              <img
                src={profilePicUrl}
                alt="Profile"
                style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", border: "2px solid #e67e22" }}
              />
            </button>

            {profileMenuOpen && (
              <div style={{
                position: "absolute",
                top: "100%",
                right: 0,
                marginTop: 8,
                background: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: 8,
                boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                zIndex: 20,
                minWidth: 200,
                overflow: "hidden"
              }}>
                <div style={{ padding: "12px 16px", fontWeight: 600, color: "#155c2b", borderBottom: "1px solid #f0f0f0" }}>
                  {userDisplayName}
                </div>
                <button
                  onClick={() => {
                    setProfileMenuOpen(false);
                  }}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    background: "transparent",
                    border: "none",
                    color: "#222",
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "background 0.2s"
                  }}
                  onMouseEnter={(e) => e.target.style.background = "#f5f5f5"}
                  onMouseLeave={(e) => e.target.style.background = "transparent"}
                >
                  Profile
                </button>
                <button
                  onClick={() => {
                    setProfileMenuOpen(false);
                  }}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    background: "transparent",
                    border: "none",
                    color: "#222",
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "background 0.2s"
                  }}
                  onMouseEnter={(e) => e.target.style.background = "#f5f5f5"}
                  onMouseLeave={(e) => e.target.style.background = "transparent"}
                >
                  Settings
                </button>
                <div style={{ height: 1, background: "#eee", margin: "6px 0" }} />
                <button
                  onClick={() => {
                    setProfileMenuOpen(false);
                    signOut(auth).then(() => navigate("/academics")).catch(() => navigate("/academics"));
                  }}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    background: "transparent",
                    border: "none",
                    color: "#e67e22",
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "background 0.2s"
                  }}
                  onMouseEnter={(e) => e.target.style.background = "#fef5f0"}
                  onMouseLeave={(e) => e.target.style.background = "transparent"}
                >
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, padding: 40, overflowY: "auto" }}>
          {page === "addDean" && (
            <div style={{ maxWidth: 600, margin: "0 auto", background: "#fff", borderRadius: 12, padding: 32, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
              <h2 style={{ marginTop: 0, fontSize: 24, fontWeight: 600, color: "#155c2b", marginBottom: 24 }}>Add Dean</h2>
              <form onSubmit={handleAddDean}>
                <input
                  type="email"
                  placeholder="Dean Email"
                  value={deanEmail}
                  onChange={e => setDeanEmail(e.target.value)}
                  required
                  style={{ width: "100%", marginBottom: 12, padding: 10, borderRadius: 6, border: "1px solid #ccc", fontSize: 14, boxSizing: "border-box" }}
                />
                <input
                  type="password"
                  placeholder="Dean Password"
                  value={deanPassword}
                  onChange={e => setDeanPassword(e.target.value)}
                  required
                  style={{ width: "100%", marginBottom: 12, padding: 10, borderRadius: 6, border: "1px solid #ccc", fontSize: 14, boxSizing: "border-box" }}
                />
                <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                  <input
                    type="text"
                    placeholder="First Name"
                    value={deanFirstName}
                    onChange={e => setDeanFirstName(e.target.value)}
                    required
                    style={{ flex: 1, padding: 10, borderRadius: 6, border: "1px solid #ccc", fontSize: 14, boxSizing: "border-box" }}
                  />
                  <input
                    type="text"
                    placeholder="Middle Name"
                    value={deanMiddleName}
                    onChange={e => setDeanMiddleName(e.target.value)}
                    style={{ flex: 1, padding: 10, borderRadius: 6, border: "1px solid #ccc", fontSize: 14, boxSizing: "border-box" }}
                  />
                </div>
                <input
                  type="text"
                  placeholder="Last Name"
                  value={deanLastName}
                  onChange={e => setDeanLastName(e.target.value)}
                  required
                  style={{ width: "100%", marginBottom: 12, padding: 10, borderRadius: 6, border: "1px solid #ccc", fontSize: 14, boxSizing: "border-box" }}
                />
                {!manualSchool ? (
                  <>
                    <select
                      value={deanSchool}
                      onChange={e => setDeanSchool(e.target.value)}
                      required
                      style={{ width: "100%", marginBottom: 12, padding: 10, borderRadius: 6, border: "1px solid #ccc", fontSize: 14, boxSizing: "border-box" }}
                    >
                      <option value="">Select School of (College)</option>
                      <option value="General Education">General Education</option>
                      {schools.filter(s => s !== "General Education").map((school, idx) => (
                        <option key={idx} value={school}>{school}</option>
                      ))}
                    </select>
                    <div style={{ marginBottom: 12 }}>
                      <button type="button" onClick={() => { setManualSchool(true); setDeanSchool(""); }} style={{ fontSize: 13, padding: "8px 12px", borderRadius: 6, background: "#f5f5f5", border: "1px solid #ccc", cursor: "pointer", fontWeight: 500 }}>Add New School of (College)</button>
                    </div>
                  </>
                ) : (
                  <>
                    <input
                      type="text"
                      placeholder="Enter School of (College) manually"
                      value={deanSchool}
                      onChange={e => setDeanSchool(e.target.value)}
                      required
                      style={{ width: "100%", marginBottom: 12, padding: 10, borderRadius: 6, border: "1px solid #ccc", fontSize: 14, boxSizing: "border-box" }}
                    />
                    <div style={{ marginBottom: 12 }}>
                      <button type="button" onClick={() => { setManualSchool(false); setDeanSchool(""); }} style={{ fontSize: 13, padding: "8px 12px", borderRadius: 6, background: "#f5f5f5", border: "1px solid #ccc", cursor: "pointer", fontWeight: 500 }}>Select from list</button>
                    </div>
                  </>
                )}
                <button type="submit" style={{ width: "100%", padding: 12, background: "#155c2b", color: "#fff", border: "none", borderRadius: 6, fontWeight: 600, fontSize: 15, cursor: "pointer", transition: "background 0.2s" }} onMouseEnter={(e) => e.target.style.background = "#0d3d1f"} onMouseLeave={(e) => e.target.style.background = "#155c2b"}>Add Dean</button>
              </form>
              {deanMsg && <div style={{ marginTop: 16, padding: 12, borderRadius: 6, background: deanMsg.includes("success") ? "#d4edda" : "#f8d7da", color: deanMsg.includes("success") ? "#155724" : "#721c24", fontSize: 14 }}>{deanMsg}</div>}
            </div>
          )}

          {page === "addRoom" && (
            <div style={{ maxWidth: 1200, margin: "0 auto" }}>
              <div style={{ background: "#fff", borderRadius: 12, padding: 32, boxShadow: "0 2px 12px rgba(0,0,0,0.06)", marginBottom: 24 }}>
                <h2 style={{ marginTop: 0, fontSize: 24, fontWeight: 600, color: "#155c2b", marginBottom: 24 }}>Add Room</h2>
                <form onSubmit={handleAddRoom}>
                  <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                    <input
                      type="text"
                      placeholder="Room Name"
                      value={roomName}
                      onChange={e => setRoomName(e.target.value)}
                      required
                      style={{ flex: 1, padding: 10, borderRadius: 6, border: "1px solid #ccc", fontSize: 14, boxSizing: "border-box" }}
                    />
                    <input
                      list="roomtype-list"
                      placeholder="Room Type"
                      value={roomType}
                      onChange={e => setRoomType(e.target.value)}
                      required
                      style={{ flex: 1, padding: 10, borderRadius: 6, border: "1px solid #ccc", fontSize: 14, boxSizing: "border-box" }}
                    />
                    <datalist id="roomtype-list">
                      {roomTypeOptions.map((type, idx) => (
                        <option key={idx} value={type} />
                      ))}
                    </datalist>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <select
                      multiple
                      value={roomDepartment}
                      onChange={e => {
                        const selected = Array.from(e.target.selectedOptions, opt => opt.value);
                        setRoomDepartment(selected);
                      }}
                      required
                      style={{ width: "100%", padding: 10, minHeight: 150, borderRadius: 6, border: "1px solid #ccc", fontSize: 14, boxSizing: "border-box" }}
                    >
                      {departments.map(dept => (
                        <option key={dept.id} value={dept.name}>{dept.name}</option>
                      ))}
                    </select>
                  </div>
                  <button type="submit" style={{ width: "100%", padding: 12, background: "#155c2b", color: "#fff", border: "none", borderRadius: 6, fontWeight: 600, fontSize: 15, cursor: "pointer", transition: "background 0.2s" }} onMouseEnter={(e) => e.target.style.background = "#0d3d1f"} onMouseLeave={(e) => e.target.style.background = "#155c2b"}>Add Room</button>
                </form>
                {roomMsg && <div style={{ marginTop: 16, padding: 12, borderRadius: 6, background: roomMsg.includes("success") ? "#d4edda" : "#f8d7da", color: roomMsg.includes("success") ? "#155724" : "#721c24", fontSize: 14 }}>{roomMsg}</div>}
              </div>

              {/* Rooms Table */}
              <div style={{ background: "#fff", borderRadius: 12, padding: 32, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                <h2 style={{ marginTop: 0, fontSize: 24, fontWeight: 600, color: "#155c2b", marginBottom: 24 }}>List of All Rooms</h2>
                <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
                  <input
                    type="text"
                    placeholder="Filter by Room Name"
                    value={roomFilterName}
                    onChange={e => setRoomFilterName(e.target.value)}
                    style={{ padding: 10, minWidth: 150, borderRadius: 6, border: "1px solid #ccc", fontSize: 14 }}
                  />
                  <select
                    value={roomFilterType}
                    onChange={e => setRoomFilterType(e.target.value)}
                    style={{ padding: 10, minWidth: 150, borderRadius: 6, border: "1px solid #ccc", fontSize: 14 }}
                  >
                    <option value="">Filter by Room Type</option>
                    {roomTypeOptions.map((type, idx) => (
                      <option key={idx} value={type}>{type}</option>
                    ))}
                  </select>
                  <select
                    value={roomFilterDept}
                    onChange={e => setRoomFilterDept(e.target.value)}
                    style={{ padding: 10, minWidth: 150, borderRadius: 6, border: "1px solid #ccc", fontSize: 14 }}
                  >
                    <option value="">Filter by Department</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.name}>{dept.name}</option>
                    ))}
                  </select>
                </div>

                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #e5e7eb", background: "#fff", fontSize: 14 }}>
                    <thead style={{ background: "#f5f7fa" }}>
                      <tr>
                        <th style={{ padding: 12, border: "1px solid #e5e7eb", fontWeight: 600, color: "#2d3748", textAlign: "left" }}>Room Name</th>
                        <th style={{ padding: 12, border: "1px solid #e5e7eb", fontWeight: 600, color: "#2d3748", textAlign: "left" }}>Room Type</th>
                        <th style={{ padding: 12, border: "1px solid #e5e7eb", fontWeight: 600, color: "#2d3748", textAlign: "left" }}>Department</th>
                        <th style={{ padding: 12, border: "1px solid #e5e7eb", fontWeight: 600, color: "#2d3748", textAlign: "left" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const totalPages = Math.ceil(filteredRooms.length / roomsPerPage);
                        const paginated = filteredRooms.slice((roomTablePage - 1) * roomsPerPage, roomTablePage * roomsPerPage);
                        
                        return paginated.length > 0 ? paginated.map((room, idx) => (
                          editRoomId === room.id ? (
                            <tr key={room.id || idx} style={{ background: idx % 2 === 0 ? '#fafbfc' : '#fff' }}>
                              <td style={{ border: "1px solid #e5e7eb", padding: 12 }}>
                                <input
                                  type="text"
                                  value={editRoomData.roomName}
                                  onChange={e => setEditRoomData({ ...editRoomData, roomName: e.target.value })}
                                  style={{ width: "100%", padding: 6, borderRadius: 4, border: "1px solid #ccc", fontSize: 14 }}
                                />
                              </td>
                              <td style={{ border: "1px solid #e5e7eb", padding: 12 }}>
                                <input
                                  type="text"
                                  value={editRoomData.roomType}
                                  onChange={e => setEditRoomData({ ...editRoomData, roomType: e.target.value })}
                                  style={{ width: "100%", padding: 6, borderRadius: 4, border: "1px solid #ccc", fontSize: 14 }}
                                />
                              </td>
                              <td style={{ border: "1px solid #e5e7eb", padding: 12 }}>
                                <input
                                  type="text"
                                  value={Array.isArray(editRoomData.departments) ? editRoomData.departments.join(", ") : editRoomData.departments}
                                  onChange={e => setEditRoomData({ ...editRoomData, departments: e.target.value.split(",").map(d => d.trim()) })}
                                  style={{ width: "100%", padding: 6, borderRadius: 4, border: "1px solid #ccc", fontSize: 14 }}
                                />
                              </td>
                              <td style={{ border: "1px solid #e5e7eb", padding: 12 }}>
                                <button onClick={handleSaveEditRoom} style={{ marginRight: 8, padding: "6px 12px", background: "#155c2b", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontWeight: 500, fontSize: 13 }}>Save</button>
                                <button onClick={() => setEditRoomId(null)} style={{ padding: "6px 12px", background: "#f5f5f5", border: "1px solid #ccc", borderRadius: 4, cursor: "pointer", fontWeight: 500, fontSize: 13 }}>Cancel</button>
                              </td>
                            </tr>
                          ) : (
                            <tr key={room.id || idx} style={{ background: idx % 2 === 0 ? '#fafbfc' : '#fff' }}>
                              <td style={{ border: "1px solid #e5e7eb", padding: 12 }}>{room.roomName}</td>
                              <td style={{ border: "1px solid #e5e7eb", padding: 12 }}>{room.roomType}</td>
                              <td style={{ border: "1px solid #e5e7eb", padding: 12 }}>{Array.isArray(room.departments) ? room.departments.join(", ") : room.department || ""}</td>
                              <td style={{ border: "1px solid #e5e7eb", padding: 12 }}>
                                <button onClick={() => handleEditRoom(room)} style={{ marginRight: 8, padding: "6px 12px", background: "#e67e22", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontWeight: 500, fontSize: 13 }}>Edit</button>
                                <button onClick={() => { setRoomDeleteError(""); setRoomToDelete(room); }} style={{ padding: "6px 12px", background: "#f8d7da", color: "#721c24", border: "none", borderRadius: 4, cursor: "pointer", fontWeight: 500, fontSize: 13 }}>Delete</button>
                              </td>
                            </tr>
                          )
                        )) : (
                          <tr>
                            <td colSpan={4} style={{ textAlign: "center", color: "#888", border: "1px solid #e5e7eb", padding: 12 }}>
                              No rooms found.
                            </td>
                          </tr>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>

                {(() => {
                  const totalPages = Math.ceil(filteredRooms.length / roomsPerPage);
                  return totalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 24, gap: 8 }}>
                      <button onClick={() => setRoomTablePage(roomTablePage - 1)} disabled={roomTablePage === 1} style={{ padding: '8px 16px', borderRadius: 6, background: "#f5f5f5", border: "1px solid #ccc", cursor: roomTablePage === 1 ? "not-allowed" : "pointer", fontWeight: 500, opacity: roomTablePage === 1 ? 0.6 : 1 }}>Prev</button>
                      <span style={{ fontWeight: 600, fontSize: 15 }}>Page {roomTablePage} of {totalPages}</span>
                      <button onClick={() => setRoomTablePage(roomTablePage + 1)} disabled={roomTablePage === totalPages} style={{ padding: '8px 16px', borderRadius: 6, background: "#f5f5f5", border: "1px solid #ccc", cursor: roomTablePage === totalPages ? "not-allowed" : "pointer", fontWeight: 500, opacity: roomTablePage === totalPages ? 0.6 : 1 }}>Next</button>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {page === "viewSchedule" && (
            <div style={{ maxWidth: "100%" }}>
              <div style={{ background: '#fff', borderRadius: 12, padding: 32, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                <h2 style={{ marginTop: 0, fontSize: 24, fontWeight: 600, color: "#155c2b", marginBottom: 24 }}>View Schedule</h2>
                <ScheduleTable />
              </div>
            </div>
          )}
        </div>
      </div>
      <DeleteConfirmationModal
        open={Boolean(roomToDelete)}
        title="Delete Room?"
        subtitle="Confirm room deletion"
        name={roomToDelete?.roomName || "this room"}
        message={`Are you sure you want to delete ${roomToDelete?.roomName || "this room"}? This action cannot be undone.`}
        detail="Please verify the room details before proceeding."
        error={roomDeleteError}
        confirmLabel="Delete Room"
        onCancel={() => { setRoomToDelete(null); setRoomDeleteError(""); }}
        onConfirm={handleDeleteRoom}
      />
    </div>
  );
}


// --- ScheduleTable component (reused) ---
function ScheduleTable() {
  const [schedules, setSchedules] = useState([]);
  const [filterSchoolYear, setFilterSchoolYear] = useState("");
  const [filterSemester, setFilterSemester] = useState("");
  const [filterYearLevel, setFilterYearLevel] = useState("");
  const [filterSection, setFilterSection] = useState("");
  const [filterCurriculum, setFilterCurriculum] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [tablePage, setTablePage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const rowsPerPage = 20;

  const fetchSchedules = async () => {
    setIsLoading(true);
    try {
      const snap = await getDocs(collection(db, "schedules"));
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setSchedules(data);
    } catch (err) {
      console.error("failed to fetch schedules", err);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchSchedules();
  }, []);

  const schoolYears = Array.from(new Set(schedules.map(s => s.schoolYear).filter(Boolean)));
  const departments = Array.from(new Set(schedules.map(s => s.department).filter(Boolean)));
  const semesters = Array.from(new Set(schedules.map(s => s.semester).filter(Boolean)));
  const yearLevels = Array.from(new Set(schedules.map(s => s.yearLevel).filter(Boolean)));
  const sections = Array.from(new Set(schedules.map(s => s.section).filter(Boolean)));

  const filtered = schedules.filter(s =>
    (!filterSchoolYear || s.schoolYear === filterSchoolYear) &&
    (!filterSemester || s.semester === filterSemester) &&
    (!filterYearLevel || s.yearLevel === filterYearLevel) &&
    (!filterSection || s.section === filterSection) &&
    (!filterCurriculum || s.curriculum === filterCurriculum) &&
    (!filterDepartment || s.department === filterDepartment)
  );

  useEffect(() => { setTablePage(1); }, [filterSchoolYear, filterSemester, filterYearLevel, filterSection, filterCurriculum, filterDepartment]);
  const totalPages = Math.ceil(filtered.length / rowsPerPage);
  const paginated = filtered.slice((tablePage - 1) * rowsPerPage, tablePage * rowsPerPage);

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginBottom: 24 }}>
        <select value={filterSchoolYear} onChange={e => setFilterSchoolYear(e.target.value)} style={{ padding: 10, minWidth: 150, borderRadius: 6, border: "1px solid #ccc", fontSize: 14 }}>
          <option value="">Filter by School Year</option>
          {schoolYears.map((year, idx) => <option key={idx} value={year}>{year}</option>)}
        </select>
        <select value={filterDepartment} onChange={e => setFilterDepartment(e.target.value)} style={{ padding: 10, minWidth: 150, borderRadius: 6, border: "1px solid #ccc", fontSize: 14 }}>
          <option value="">Filter by Department</option>
          {departments.map((dept, idx) => <option key={idx} value={dept}>{dept}</option>)}
        </select>
        <select value={filterSemester} onChange={e => setFilterSemester(e.target.value)} style={{ padding: 10, minWidth: 150, borderRadius: 6, border: "1px solid #ccc", fontSize: 14 }}>
          <option value="">Filter by Semester</option>
          {semesters.map((sem, idx) => <option key={idx} value={sem}>{sem}</option>)}
        </select>
        <select value={filterYearLevel} onChange={e => setFilterYearLevel(e.target.value)} style={{ padding: 10, minWidth: 150, borderRadius: 6, border: "1px solid #ccc", fontSize: 14 }}>
          <option value="">Filter by Year Level</option>
          {yearLevels.map((yl, idx) => <option key={idx} value={yl}>{yl}</option>)}
        </select>
        <select value={filterSection} onChange={e => setFilterSection(e.target.value)} style={{ padding: 10, minWidth: 150, borderRadius: 6, border: "1px solid #ccc", fontSize: 14 }}>
          <option value="">Filter by Section</option>
          {sections.map((section, idx) => <option key={idx} value={section}>{section}</option>)}
        </select>
        <button onClick={() => {
          setFilterSchoolYear("");
          setFilterSemester("");
          setFilterYearLevel("");
          setFilterSection("");
          setFilterCurriculum("");
          setFilterDepartment("");
        }} style={{ padding: "10px 16px", borderRadius: 6, background: "#f5f5f5", border: "1px solid #ccc", cursor: "pointer", fontWeight: 500, fontSize: 14 }}>Clear Filters</button>
        <button onClick={fetchSchedules} disabled={isLoading} style={{ padding: "10px 16px", background: "#155c2b", color: "#fff", border: "none", borderRadius: 6, cursor: isLoading ? "not-allowed" : "pointer", fontWeight: 600, fontSize: 14, opacity: isLoading ? 0.6 : 1 }}>
          {isLoading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <div style={{ width: '100%', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e5e7eb', background: '#fff', fontSize: 14, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '16%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '5%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '5%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead style={{ background: '#f5f7fa' }}>
            <tr>
              <th style={{ padding: 12, border: '1px solid #e5e7eb', fontWeight: 600, color: "#2d3748", textAlign: "left" }}>Department</th>
              <th style={{ padding: 12, border: '1px solid #e5e7eb', fontWeight: 600, color: "#2d3748", textAlign: "left" }}>Course ID</th>
              <th style={{ padding: 12, border: '1px solid #e5e7eb', fontWeight: 600, color: "#2d3748", textAlign: "left" }}>Course Name</th>
              <th style={{ padding: 12, border: '1px solid #e5e7eb', fontWeight: 600, color: "#2d3748", textAlign: "left" }}>Section</th>
              <th style={{ padding: 12, border: '1px solid #e5e7eb', fontWeight: 600, color: "#2d3748", textAlign: "left" }}>Day</th>
              <th style={{ padding: 12, border: '1px solid #e5e7eb', fontWeight: 600, color: "#2d3748", textAlign: "left" }}>Time</th>
              <th style={{ padding: 12, border: '1px solid #e5e7eb', fontWeight: 600, color: "#2d3748", textAlign: "left" }}>Room</th>
              <th style={{ padding: 12, border: '1px solid #e5e7eb', fontWeight: 600, color: "#2d3748", textAlign: "left" }}>Professor</th>
            </tr>
          </thead>
          <tbody>
            {paginated.length > 0 ? paginated.map((sched, idx) => (
              <tr key={sched.id || idx} style={{ background: idx % 2 === 0 ? '#fafbfc' : '#fff' }}>
                <td style={{ border: "1px solid #e5e7eb", padding: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sched.department}</td>
                <td style={{ border: "1px solid #e5e7eb", padding: 12 }}>{sched.courseId}</td>
                <td style={{ border: "1px solid #e5e7eb", padding: 12 }}>{sched.courseName}</td>
                <td style={{ border: "1px solid #e5e7eb", padding: 12 }}>{sched.section}</td>
                <td style={{ border: "1px solid #e5e7eb", padding: 12 }}>{sched.day || sched.days}</td>
                <td style={{ border: "1px solid #e5e7eb", padding: 12 }}>{sched.time || sched.startEnd}</td>
                <td style={{ border: "1px solid #e5e7eb", padding: 12 }}>{sched.room}</td>
                <td style={{ border: "1px solid #e5e7eb", padding: 12 }}>{sched.prof}</td>
              </tr>
            )) : (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", color: "#888", border: "1px solid #e5e7eb", padding: 12 }}>
                  No schedules found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 24, gap: 8 }}>
          <button onClick={() => setTablePage(tablePage - 1)} disabled={tablePage === 1} style={{ padding: '8px 16px', borderRadius: 6, background: "#f5f5f5", border: "1px solid #ccc", cursor: tablePage === 1 ? "not-allowed" : "pointer", fontWeight: 500, opacity: tablePage === 1 ? 0.6 : 1 }}>Prev</button>
          <span style={{ fontWeight: 600, fontSize: 15 }}>Page {tablePage} of {totalPages}</span>
          <button onClick={() => setTablePage(tablePage + 1)} disabled={tablePage === totalPages} style={{ padding: '8px 16px', borderRadius: 6, background: "#f5f5f5", border: "1px solid #ccc", cursor: tablePage === totalPages ? "not-allowed" : "pointer", fontWeight: 500, opacity: tablePage === totalPages ? 0.6 : 1 }}>Next</button>
        </div>
      )}
    </div>
  );
}